import {
  CountrySelectorForHeader,
  InputWithLabel,
  SelectWithLabel,
  TextAreaWithLabel
} from '@adotel/components'
import { useMutation } from '@apollo/client'
import { Grid, MenuItem, Switch, Typography } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom'
import NoMeetingRoomIcon from '@material-ui/icons/NoMeetingRoom'
import { FormikErrors, FormikTouched } from 'formik'
import React, { useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { ConfirmActionModal } from '../../components/ConfirmActionModal'
import SectionTitle from '../../components/SectionTitle'
import { DELETE_ROOMTYPE } from '../../graphql/roomtypeDelete'
import { ROOMTYPE_MODIFY_ACTIVE_MUTATION } from '../../graphql/roomtypeModify'
import { countries, CountryType } from '../../helpers/countriesList'
import { entityCodeRegex, persianRegex } from '../../helpers/regex'
import {
  isFaAvailableInLanguages,
  getRoleFromStorage
} from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import { RoomType } from '../../types/roomtype'

interface RoomtypeBasicDetailsProps {
  values: RoomType
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<RoomType>
  touched: FormikTouched<RoomType>
  handleBlur: any
  isSubmitting: any
  isPhysical: boolean
  propertyCode: string
  classes: any
}

export const RoomtypeBasicDetails: React.FC<RoomtypeBasicDetailsProps> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  isPhysical,
  propertyCode,
  classes
}) => {
  const history = useHistory()
  const params = useParams<{
    accommodationID: string
    roomTypeCode: string
    propertyCode: string
    roomTypeId: string
  }>()
  const [selectedCountryForName, setselectedCountryForName] =
    useState<CountryType>(countries[0])
  const [selectedCountryForDescription, setselectedCountryForDescription] =
    useState<CountryType>(countries[0])
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)
  const [activeModalIsOpen, setActiveModalIsOpen] = useState(false)

  const [deleteRoomtype, { loading: loadingForDeleteRoom }] = useMutation(
    DELETE_ROOMTYPE,
    {
      onCompleted: (resp) => {
        toast.success(resp.deleteRoomType.message)
        setDeleteModalIsOpen(false)
        history.push(
          `/${lang}/accommodations/${params.accommodationID}/${params.propertyCode}/room-types/list`
        )
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  const [modifyActiveRoomtype, { loading: loadingForModifyActive }] =
    useMutation(ROOMTYPE_MODIFY_ACTIVE_MUTATION, {
      onCompleted: (resp) => {
        toast.success(resp.modifyActiveRoomType.message)
        setActiveModalIsOpen(false)
        history.push(
          `/${lang}/accommodations/${params.accommodationID}/${params.propertyCode}/room-types/list`
        )
      },
      onError: (err) => {
        toast.error(err.message)
      }
    })

  const isRoleAdmin = getRoleFromStorage() === ROLE.ADMIN

  const isPersianName = persianRegex.test(
    values.basicDetails.nameTranslatedList.find(
      (item) => item.label === selectedCountryForName.label
    )?.value || ''
  )

  return (
    <>
      <div className={classes.titleContainer}>
        <SectionTitle
          title="Basic Details"
          marginBottom
          deleteIcon={!!params.roomTypeCode ? true : false}
          onClickOnDeleteIcon={() => {
            setDeleteModalIsOpen(true)
          }}
          disabled={values.basicDetails.deleted}
        />
      </div>
      <div className={classes.fieldsContainer}>
        <SelectWithLabel
          name="basicDetails.type"
          label={getTranslate('Type')}
          fullWidth
          value={values.basicDetails.type}
          onChange={(event) =>
            setFieldValue('basicDetails.type', event.target.value)
          }
          error={
            (touched.basicDetails?.type && !!errors.basicDetails?.type) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.type
              ? getTranslate(errors.basicDetails?.type || '')
              : undefined
          }
          disabled={!!params.roomTypeCode}
          size="small">
          <MenuItem key="Physical" value="Physical">
            {getTranslate('Physical')}
          </MenuItem>
          <MenuItem key="Virtual" value="Virtual">
            {getTranslate('Virtual')}
          </MenuItem>
        </SelectWithLabel>
        <InputWithLabel
          label={getTranslate('Name')}
          placeholder={getTranslate('Enter the room type name')}
          autoComplete="off"
          value={values.basicDetails.name}
          name="basicDetails.name"
          id="basicDetails.name"
          onChange={(event) =>
            setFieldValue('basicDetails.name', event.currentTarget.value)
          }
          error={
            (touched.basicDetails?.name && !!errors.basicDetails?.name) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.name
              ? getTranslate(errors.basicDetails?.name || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        {isFaAvailableInLanguages() ? (
          <InputWithLabel
            style={isPersianName ? { fontFamily: 'IranSans' } : undefined}
            label={`${
              lang === 'en' ? selectedCountryForName.language : ''
            } ${getTranslate('Name')} ${
              lang === 'fa' && selectedCountryForName.code === 'IR'
                ? selectedCountryForName.nativeLanguage
                : lang === 'fa'
                ? selectedCountryForName.language
                : ''
            }`}
            placeholder={
              lang === 'fa' && selectedCountryForName.code === 'IR'
                ? `${getTranslate('Enter the room type name in')} ${
                    selectedCountryForName.nativeLanguage
                  }`
                : `${getTranslate('Enter the room type name in')} ${
                    selectedCountryForName.language
                  }`
            }
            autoComplete="off"
            id={`basicDetails.nameTranslatedList[${values.basicDetails.nameTranslatedList.findIndex(
              (item) => item.label === selectedCountryForName.label
            )}].value`}
            name={`basicDetails.nameTranslatedList[${values.basicDetails.nameTranslatedList.findIndex(
              (item) => item.label === selectedCountryForName.label
            )}].value`}
            value={
              values.basicDetails.nameTranslatedList.find(
                (item) => item.label === selectedCountryForName.label
              )?.value
            }
            onChange={(event: {
              currentTarget: { value: React.SetStateAction<string> }
            }) => {
              setFieldValue(
                `basicDetails.nameTranslatedList[${values.basicDetails.nameTranslatedList.findIndex(
                  (item) => item.label === selectedCountryForName.label
                )}].value`,
                event.currentTarget.value
              )
            }}
            error={
              (touched.basicDetails?.nameTranslatedList &&
                !!errors.basicDetails?.nameTranslatedList) ||
              undefined
            }
            helpertext={
              touched.basicDetails?.nameTranslatedList
                ? (
                    errors.basicDetails?.nameTranslatedList as FormikErrors<{
                      value: string
                    }>[]
                  )
                    ?.filter((item) => item)
                    .map((item) => getTranslate(item.value || ''))
                    .join(' ')
                : undefined
            }
            onBlur={handleBlur}
            disabled={isSubmitting}
            inputSize="small"
            rightHeader={
              <CountrySelectorForHeader
                selectedCountry={selectedCountryForName}
                onChange={(item: CountryType) => {
                  const translatedName = {
                    label: item.label,
                    nativeLabel: item.nativeLanguage,
                    value: ''
                  }
                  const tempTranslatedArr = [
                    ...values.basicDetails.nameTranslatedList,
                    values.basicDetails.nameTranslatedList.find(
                      (singleName) => singleName.label === item.label
                    )
                      ? undefined
                      : translatedName
                  ]
                  setFieldValue(
                    'basicDetails.nameTranslatedList',
                    tempTranslatedArr
                  )
                  setselectedCountryForName(item)
                }}
              />
            }
          />
        ) : (
          <div />
        )}
      </div>
      <div className={classes.fieldsContainer}>
        <InputWithLabel
          label={getTranslate('Code')}
          placeholder={getTranslate('Choose a code for room type')}
          autoComplete="off"
          name="basicDetails.code"
          id="basicDetails.code"
          value={values.basicDetails.displayRoomTypeCode}
          onChange={(event: { currentTarget: { value: string } }) => {
            const rawInput = event.currentTarget.value
            const inputValue = rawInput.replace(/\s+/g, '') // remove all whitespace (including spaces, tabs)

            if (entityCodeRegex.test(inputValue) || inputValue === '') {
              setFieldValue('basicDetails.code', inputValue)
              setFieldValue('basicDetails.displayRoomTypeCode', inputValue)
            }
          }}
          error={
            (touched.basicDetails?.roomTypeCode &&
              !!errors.basicDetails?.roomTypeCode) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.roomTypeCode
              ? getTranslate(errors.basicDetails?.roomTypeCode || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting || !!params.roomTypeCode}
          inputSize="small"
        />
        {!!params.roomTypeCode || isPhysical ? (
          <>
            <InputWithLabel
              label={getTranslate('Default Rack Rate')}
              placeholder={getTranslate('Enter the default Rack Rate')}
              autoComplete="off"
              type="number"
              name="basicDetails.defaultRackRate"
              id="basicDetails.defaultRackRate"
              value={values.basicDetails.defaultRackRate as number}
              onChange={(event) =>
                setFieldValue(
                  'basicDetails.defaultRackRate',
                  parseInt(event.currentTarget.value, 10)
                )
              }
              onBlur={handleBlur}
              disabled={isSubmitting || !!params.roomTypeCode}
              inputSize="small"
            />
            <div />
          </>
        ) : (
          <>
            <div />
            <div />
          </>
        )}
      </div>
      <div className={classes.fieldsContainer}>
        <TextAreaWithLabel
          label={getTranslate('Description')}
          placeholder={getTranslate('Enter the room type description')}
          autoComplete="off"
          name="basicDetails.description"
          id="basicDetails.description"
          value={values.basicDetails.description}
          onChange={(event) =>
            setFieldValue('basicDetails.description', event.currentTarget.value)
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          rows={7}
        />
        {isFaAvailableInLanguages() && (
          <TextAreaWithLabel
            placeholder={
              lang === 'fa' && selectedCountryForDescription.code === 'IR'
                ? `${getTranslate('Enter the accommodation description for')} ${
                    selectedCountryForDescription.nativeLanguage
                  }`
                : `${getTranslate('Enter the accommodation description for')} ${
                    selectedCountryForDescription.language
                  }`
            }
            label={`${
              lang === 'en' ? selectedCountryForDescription.language : ''
            } ${getTranslate('Description')} ${
              lang === 'fa' && selectedCountryForName.code === 'IR'
                ? selectedCountryForDescription.nativeLanguage
                : lang === 'fa'
                ? selectedCountryForName.language
                : ''
            }`}
            autoComplete="off"
            id={`basicDetails.descriptionTranslatedList[${values.basicDetails.descriptionTranslatedList.findIndex(
              (item) => item.label === selectedCountryForDescription.label
            )}].value`}
            name={`basicDetails.descriptionTranslatedList[${values.basicDetails.descriptionTranslatedList.findIndex(
              (item) => item.label === selectedCountryForDescription.label
            )}].value`}
            value={
              values.basicDetails.descriptionTranslatedList.find(
                (item) => item.label === selectedCountryForDescription.label
              )?.value
            }
            onChange={(event: {
              currentTarget: { value: React.SetStateAction<string> }
            }) =>
              setFieldValue(
                `basicDetails.descriptionTranslatedList[${values.basicDetails.nameTranslatedList.findIndex(
                  (item) => item.label === selectedCountryForName.label
                )}].value`,
                event.currentTarget.value
              )
            }
            onBlur={handleBlur}
            disabled={isSubmitting}
            inputSize="small"
            rows={7}
            rightHeader={
              <CountrySelectorForHeader
                selectedCountry={selectedCountryForDescription}
                onChange={(item: CountryType) => {
                  const translatedDescription = {
                    label: item.label,
                    nativeLabel: item.nativeLanguage,
                    value: ''
                  }
                  const tempTranslatedArr = [
                    ...values.basicDetails.descriptionTranslatedList,
                    values.basicDetails.descriptionTranslatedList.find(
                      (singleDescription) =>
                        singleDescription.label === item.label
                    )
                      ? undefined
                      : translatedDescription
                  ]
                  setFieldValue(
                    'basicDetails.descriptionTranslatedList',
                    tempTranslatedArr
                  )
                  setselectedCountryForDescription(item)
                }}
              />
            }
          />
        )}
        {isRoleAdmin && (
          <div className={classes.switchContainer}>
            <Typography variant="h4">
              {getTranslate('Is this room active?')}
            </Typography>
            <Typography component="div" className={classes.switch}>
              <Grid
                component="label"
                container
                alignItems="center"
                justify="flex-start">
                <Grid item>{getTranslate('No')}</Grid>
                <Grid item>
                  <Switch
                    name="active"
                    color="primary"
                    onChange={() => {
                      setActiveModalIsOpen(true)
                    }}
                    checked={values.basicDetails.active}
                    disabled={
                      !params.roomTypeCode || values.basicDetails.deleted
                    }
                  />
                </Grid>
                <Grid item>{getTranslate('Yes')}</Grid>
              </Grid>
            </Typography>
          </div>
        )}
      </div>
      {deleteModalIsOpen && (
        <ConfirmActionModal
          icon={<DeleteIcon className={classes.icon} />}
          title={'Room is about to be deleted'}
          subtitle={'Are you sure you want to delete it permanently?'}
          signature={'Delete Me'}
          onClose={() => setDeleteModalIsOpen(false)}
          isOpen={deleteModalIsOpen}
          onClick={() => {
            deleteRoomtype({
              variables: {
                input: {
                  propertyCode: propertyCode,
                  roomTypeCode: values.basicDetails.roomTypeCode,
                  roomTypeNames: {
                    EN: values.basicDetails.name,
                    FA: values.basicDetails.nameTranslatedList[0].value
                  }
                }
              }
            })
          }}
          loading={loadingForDeleteRoom}
        />
      )}
      {activeModalIsOpen && (
        <ConfirmActionModal
          icon={
            values.basicDetails.active ? (
              <NoMeetingRoomIcon className={classes.icon} />
            ) : (
              <MeetingRoomIcon className={classes.icon} />
            )
          }
          title={
            values.basicDetails.active
              ? getTranslate('The room is about to be deactivated')
              : getTranslate('The room is about to be activated')
          }
          subtitle={
            values.basicDetails.active
              ? getTranslate('Are you sure you want to deactive it?')
              : getTranslate('Are you sure you want to active it?')
          }
          signature={values.basicDetails.active ? 'Deactive Me' : 'Active Me'}
          onClose={() => setActiveModalIsOpen(false)}
          isOpen={activeModalIsOpen}
          onClick={() => {
            modifyActiveRoomtype({
              variables: {
                propertyCode: propertyCode,
                roomTypeCode: values.basicDetails.roomTypeCode,
                active: !values.basicDetails.active
              }
            })
          }}
          loading={loadingForModifyActive}
        />
      )}
    </>
  )
}
